import { useState } from 'react'
import { useConnection } from '@solana/wallet-adapter-react'
import { useDagProgram } from './useDagProgram'
import { useAccount } from './useAccount'
import { api } from '~/services/api'
import { CreateWorkRequest, CreateWorkResponse } from '~/types/api'
import { PublicKey, SystemProgram } from '@solana/web3.js'

interface WorkCreationData {
  proposalId: string
  proposalPda: string
  tokenAddress: string
}

interface WorkCreationResult {
  txSignature: string
  work: CreateWorkResponse['work']
}

interface TransactionResult {
  txSignature: string
  workPda: string
  proposalId: string
}

export const useWorkCreation = () => {
  const { connection } = useConnection()
  const { program, getProjectPda } = useDagProgram()
  const { account, isAuthenticated, checkGitHubConnection } = useAccount()
  const [isLoading, setIsLoading] = useState(false)
  const [isConfirming, setIsConfirming] = useState(false)
  const [isCreatingWork, setIsCreatingWork] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [transactionResult, setTransactionResult] = useState<TransactionResult | null>(null)

  const createWork = async (data: WorkCreationData): Promise<WorkCreationResult> => {
    if (!account?.address) {
      throw new Error('Wallet not connected')
    }

    if (!isAuthenticated) {
      throw new Error('Please sign in first')
    }

    if (!program) {
      throw new Error('Program not initialized')
    }

    setIsLoading(true)
    setError(null)
    setTransactionResult(null)
    setIsConfirming(false)
    setIsCreatingWork(false)

    try {
      // Check GitHub connection
      const hasGitHub = await checkGitHubConnection()
      if (!hasGitHub) {
        throw new Error('GitHub account must be connected to create work')
      }

      // Step 1: Send transaction and get signature
      console.log('Creating work item on-chain...')
      
      const projectPda = getProjectPda(data.tokenAddress)
      const proposalPdaKey = new PublicKey(data.proposalPda)
      
      // Get the proposal account to read the work item count
      const proposalAccount = await program.account.proposal.fetch(proposalPdaKey)
      const workItemId = proposalAccount.workItemCount
      
      // Derive the work item PDA using the correct seeds
      const [workItemPda] = PublicKey.findProgramAddressSync(
        [Buffer.from("work"), proposalPdaKey.toBuffer(), workItemId.toBuffer('le', 8)],
        program.programId
      )

      const txSignature = await program.methods
        .createWorkItem("Work Item", "Work item description") // These will be updated by the API
        .accountsStrict({
          project: projectPda,
          proposal: proposalPdaKey,
          workItem: workItemPda,
          creator: new PublicKey(account.address),
          systemProgram: SystemProgram.programId,
        })
        .rpc()

      console.log('Transaction sent:', {
        workItemPda: workItemPda.toBase58(),
        txSignature
      })

      const txResult: TransactionResult = {
        txSignature,
        workPda: workItemPda.toBase58(),
        proposalId: data.proposalId
      }
      setTransactionResult(txResult)

      // Step 2: Create work through API
      setIsCreatingWork(true)
      const workResponse = await createWorkWithRetry(txResult.workPda)
      setIsCreatingWork(false)

      console.log('Work created through API:', workResponse)

      return {
        txSignature,
        work: workResponse.work,
      }
    } catch (err) {
      console.error('Work creation failed:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to create work'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setIsLoading(false)
      setIsConfirming(false)
      setIsCreatingWork(false)
    }
  }

  const createWorkWithRetry = async (
    workPda: string
  ): Promise<CreateWorkResponse> => {
    const workRequest: CreateWorkRequest = {
      workPda
    }

    console.log('Creating work through API...')
    return await api.works.create(workRequest)
  }

  const retryWorkCreation = async (): Promise<CreateWorkResponse> => {
    if (!transactionResult) {
      throw new Error('No transaction result available for retry')
    }

    setIsCreatingWork(true)
    setError(null)

    try {
      const workResponse = await createWorkWithRetry(transactionResult.workPda)
      console.log('Work created successfully on retry:', workResponse)
      return workResponse
    } catch (err) {
      console.error('Work creation retry failed:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to create work'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setIsCreatingWork(false)
    }
  }

  return {
    createWork,
    retryWorkCreation,
    isLoading,
    isConfirming,
    isCreatingWork,
    error,
    transactionResult,
    clearError: () => setError(null),
  }
} 